import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type PropsWithChildren } from "react";
import { createPortal } from "react-dom";
import { JitsiMeeting } from "@jitsi/react-sdk";
import type IJitsiMeetExternalApi from "@jitsi/react-sdk/lib/types/IJitsiMeetExternalApi";
import { TELEMED_DOCTOR_JITSI_CONFIG } from "./jitsiConfig";
import Badge from "../../components/Badge";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import Tooltip from "../../components/Tooltip";
import { createTelemedMeeting, type TelemedMeetingSession, type TreatmentId } from "../../api/services/clinic/telemed";

const PANEL_EDGE_GAP = 24;
const DEFAULT_PANEL_WIDTH = 360;
const DEFAULT_PANEL_HEIGHT = 640;
const MIN_PANEL_WIDTH = 360;
const MIN_PANEL_HEIGHT = 300;
const PRESSCRIBE_DOCK_HEIGHT = 140;
const PRESSCRIBE_DOCK_GAP = 16;

type CallStatus = "ready" | "connecting" | "joining" | "active";

type PanelPosition = { x: number | null; y: number };
type PanelSize = { width: number; height: number };

type TelemedVisit = {
    [key: string]: unknown;
    treatment?: {
        doctor_name?: string;
        id?: TreatmentId;
    };
};

type TelemedOverlayRequest = {
    doctorName?: string;
    visit?: TelemedVisit;
    visitId?: TreatmentId | "";
};

type TelemedOverlayContextValue = {
    closeTelemedOverlay: () => void;
    isTelemedOverlayOpen: boolean;
    openTelemedOverlay: (request: TelemedOverlayRequest) => void;
    telemedRequest: TelemedOverlayRequest | null;
};

type DragState = {
    height: number;
    originX: number;
    originY: number;
    pointerId: number;
    startX: number;
    startY: number;
    width: number;
    x?: number;
    y?: number;
};

type ResizeState = {
    height: number;
    originX: number;
    originY: number;
    pointerId: number;
    startHeight: number;
    startWidth: number;
    startX: number;
    startY: number;
    width: number;
};

const TelemedOverlayContext = createContext<TelemedOverlayContextValue>({
    closeTelemedOverlay: () => {},
    isTelemedOverlayOpen: false,
    openTelemedOverlay: () => {},
    telemedRequest: null,
});

function clamp(value: number, minimum: number, maximum: number) {
    return Math.min(Math.max(value, minimum), maximum);
}

function formatTime(seconds: number) {
    const minutes = Math.floor(seconds / 60).toString().padStart(2, "0");
    const remainingSeconds = (seconds % 60).toString().padStart(2, "0");
    return `${minutes}:${remainingSeconds}`;
}

function getViewportPanelSize(width: number, height: number): PanelSize {
    const maxWidth = Math.max(0, window.innerWidth - PANEL_EDGE_GAP * 2);
    const maxHeight = Math.max(0, window.innerHeight - PANEL_EDGE_GAP * 2);

    return {
        width: clamp(width, Math.min(MIN_PANEL_WIDTH, maxWidth), maxWidth),
        height: clamp(height, Math.min(MIN_PANEL_HEIGHT, maxHeight), maxHeight),
    };
}

export function TelemedOverlayProvider({ children }: PropsWithChildren) {
    const [isTelemedOverlayOpen, setIsTelemedOverlayOpen] = useState(false);
    const [telemedRequest, setTelemedRequest] = useState<TelemedOverlayRequest | null>(null);

    const openTelemedOverlay = useCallback((request: TelemedOverlayRequest) => {
        setTelemedRequest(request);
        setIsTelemedOverlayOpen(true);
    }, []);

    const closeTelemedOverlay = useCallback(() => {
        setIsTelemedOverlayOpen(false);
        setTelemedRequest(null);
    }, []);

    const value = useMemo(() => ({
        closeTelemedOverlay,
        isTelemedOverlayOpen,
        openTelemedOverlay,
        telemedRequest,
    }), [closeTelemedOverlay, isTelemedOverlayOpen, openTelemedOverlay, telemedRequest]);

    return <TelemedOverlayContext.Provider value={value}>{children}</TelemedOverlayContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTelemedOverlay() {
    return useContext(TelemedOverlayContext);
}

/**
 * Telemedicine entry card and modeless floating video-call panel.
 * The floating panel opens Jitsi's own prejoin screen for camera and microphone setup.
 */
export function FloatingTelemedOverlay({ shouldDockAbovePresscribe = false }: { shouldDockAbovePresscribe?: boolean }) {
    const {
        closeTelemedOverlay,
        isTelemedOverlayOpen: isOverlayOpen,
        telemedRequest,
    } = useTelemedOverlay();
    const visit = telemedRequest?.visit;
    const doctorName = telemedRequest?.doctorName || "";
    const visitId = telemedRequest?.visitId || "";
    const [callStatus, setCallStatus] = useState<CallStatus>("ready");
    const [durationSeconds, setDurationSeconds] = useState(0);
    const [panelPosition, setPanelPosition] = useState<PanelPosition>({ x: null, y: PANEL_EDGE_GAP });
    const [panelSize, setPanelSize] = useState(() => getViewportPanelSize(DEFAULT_PANEL_WIDTH, DEFAULT_PANEL_HEIGHT));
    const [telemedMeeting, setTelemedMeeting] = useState<TelemedMeetingSession | null>(null);
    const [callError, setCallError] = useState("");
    const [isInviteLinkCopied, setIsInviteLinkCopied] = useState(false);

    const panelRef = useRef<HTMLElement | null>(null);
    const dragStateRef = useRef<DragState | null>(null);
    const resizeStateRef = useRef<ResizeState | null>(null);
    const timerRef = useRef<number | null>(null);
    const connectingTimeoutRef = useRef<number | null>(null);
    const jitsiApiRef = useRef<IJitsiMeetExternalApi | null>(null);
    const hasRequestedHangupRef = useRef(false);
    const inviteCopyTimeoutRef = useRef<number | null>(null);
    const hasDockedAbovePresscribeRef = useRef(false);

    const effectiveDoctorName = doctorName || visit?.treatment?.doctor_name || "";
    const effectiveTreatmentId = visit?.treatment?.id || visitId;

    const disposeJitsi = useCallback(() => {
        const api = jitsiApiRef.current;
        jitsiApiRef.current = null;

        if (!api) return;

        try {
            api.dispose();
        } catch (error) {
            console.info("Jitsi dispose info:", error);
        }
    }, []);

    const clearConnectingTimeout = useCallback(() => {
        if (!connectingTimeoutRef.current) return;
        window.clearTimeout(connectingTimeoutRef.current);
        connectingTimeoutRef.current = null;
    }, []);

    const clearInviteCopyState = useCallback(() => {
        if (inviteCopyTimeoutRef.current) {
            window.clearTimeout(inviteCopyTimeoutRef.current);
            inviteCopyTimeoutRef.current = null;
        }
        setIsInviteLinkCopied(false);
    }, []);

    const handleCopyInviteLink = useCallback(async () => {
        const inviteUrl = telemedMeeting?.invite_url;
        if (!inviteUrl || !navigator.clipboard?.writeText) return;

        try {
            await navigator.clipboard.writeText(inviteUrl);
            setIsInviteLinkCopied(true);
            if (inviteCopyTimeoutRef.current) window.clearTimeout(inviteCopyTimeoutRef.current);
            inviteCopyTimeoutRef.current = window.setTimeout(() => {
                inviteCopyTimeoutRef.current = null;
                setIsInviteLinkCopied(false);
            }, 2000);
        } catch (error) {
            console.info("Invite link copy info:", error);
        }
    }, [telemedMeeting?.invite_url]);

    const handleCloseOverlay = useCallback(() => {
        clearConnectingTimeout();
        disposeJitsi();
        hasRequestedHangupRef.current = false;
        setCallStatus("ready");
        setDurationSeconds(0);
        setTelemedMeeting(null);
        setCallError("");
        clearInviteCopyState();
        closeTelemedOverlay();
    }, [clearConnectingTimeout, clearInviteCopyState, closeTelemedOverlay, disposeJitsi]);

    useEffect(() => {
        if (callStatus === "active") {
            timerRef.current = window.setInterval(() => {
                setDurationSeconds((current) => current + 1);
            }, 1000);
        }

        return () => {
            if (timerRef.current) {
                window.clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [callStatus]);

    useEffect(() => {
        return () => {
            clearConnectingTimeout();
            disposeJitsi();
            if (inviteCopyTimeoutRef.current) window.clearTimeout(inviteCopyTimeoutRef.current);
        };
    }, [clearConnectingTimeout, disposeJitsi]);

    useEffect(() => {
        if (!isOverlayOpen) return undefined;

        function keepPanelInViewport() {
            const panel = panelRef.current;
            if (!panel) return;

            const rect = panel.getBoundingClientRect();
            const nextX = clamp(
                rect.left,
                PANEL_EDGE_GAP,
                Math.max(PANEL_EDGE_GAP, window.innerWidth - rect.width - PANEL_EDGE_GAP)
            );
            const nextY = clamp(
                rect.top,
                PANEL_EDGE_GAP,
                Math.max(PANEL_EDGE_GAP, window.innerHeight - rect.height - PANEL_EDGE_GAP)
            );

            setPanelSize((current) => {
                const nextSize = getViewportPanelSize(current.width, current.height);
                if (current.width === nextSize.width && current.height === nextSize.height) return current;
                return nextSize;
            });

            setPanelPosition((current) => {
                if (
                    current.x === null &&
                    rect.left >= PANEL_EDGE_GAP &&
                    rect.right <= window.innerWidth - PANEL_EDGE_GAP &&
                    rect.top >= PANEL_EDGE_GAP &&
                    rect.bottom <= window.innerHeight - PANEL_EDGE_GAP
                ) {
                    return current;
                }

                if (current.x === nextX && current.y === nextY) return current;
                return { x: nextX, y: nextY };
            });
        }

        window.addEventListener("resize", keepPanelInViewport);
        window.addEventListener("keydown", handleEscapeKey);

        function handleEscapeKey(event: KeyboardEvent) {
            if (event.key === "Escape") handleCloseOverlay();
        }

        return () => {
            window.removeEventListener("resize", keepPanelInViewport);
            window.removeEventListener("keydown", handleEscapeKey);
        };
    }, [handleCloseOverlay, isOverlayOpen]);

    const handleJoinCall = useCallback(async () => {
        clearConnectingTimeout();
        clearInviteCopyState();
        setCallError("");
        if (!effectiveTreatmentId) {
            setCallError("ไม่พบรหัสการรักษาสำหรับห้องวิดีโอคอล");
            return;
        }
        setCallStatus("connecting");
        try {
            const meeting = await createTelemedMeeting(effectiveTreatmentId);
            if (!meeting?.domain || !meeting?.room_name || !meeting?.jwt) {
                throw new Error("ไม่ได้รับข้อมูลห้องวิดีโอคอลจากเซิร์ฟเวอร์");
            }
            setTelemedMeeting(meeting);
            setCallStatus("joining");
        } catch (error) {
            setCallStatus("ready");
            setCallError(error instanceof Error ? error.message : "ไม่สามารถสร้างห้องวิดีโอคอลได้");
        }
    }, [clearConnectingTimeout, clearInviteCopyState, effectiveTreatmentId]);

    useEffect(() => {
        if (!isOverlayOpen || !telemedRequest) return;

        setPanelPosition({ x: null, y: PANEL_EDGE_GAP });
        setPanelSize(getViewportPanelSize(DEFAULT_PANEL_WIDTH, DEFAULT_PANEL_HEIGHT));
        setCallStatus("ready");
        setDurationSeconds(0);
        void handleJoinCall();
    }, [handleJoinCall, isOverlayOpen, telemedRequest]);

    useEffect(() => {
        if (!isOverlayOpen || !shouldDockAbovePresscribe) {
            hasDockedAbovePresscribeRef.current = false;
            return;
        }
        if (hasDockedAbovePresscribeRef.current) return;

        hasDockedAbovePresscribeRef.current = true;

        const maximumDockHeight = Math.max(
            PANEL_EDGE_GAP,
            window.innerHeight - PRESSCRIBE_DOCK_HEIGHT - PRESSCRIBE_DOCK_GAP - PANEL_EDGE_GAP
        );
        const dockedHeight = Math.min(panelSize.height, maximumDockHeight);
        setPanelSize((current) => (
            current.height === dockedHeight ? current : { ...current, height: dockedHeight }
        ));
        setPanelPosition({
            x: 0,
            y: Math.max(
                PANEL_EDGE_GAP,
                window.innerHeight - dockedHeight - PRESSCRIBE_DOCK_HEIGHT - PRESSCRIBE_DOCK_GAP
            ),
        });
    }, [isOverlayOpen, panelSize.height, shouldDockAbovePresscribe]);

    function handleEndCall() {
        const api = jitsiApiRef.current;
        if (!api) {
            handleCloseOverlay();
            return;
        }
        if (hasRequestedHangupRef.current) return;

        hasRequestedHangupRef.current = true;
        try {
            api.executeCommand("hangup");
            // Jitsi emits videoConferenceLeft after the command. Keep a
            // fallback so a failed iframe callback cannot leave the panel open.
            connectingTimeoutRef.current = window.setTimeout(handleCloseOverlay, 1000);
        } catch (error) {
            console.info("Jitsi hangup info:", error);
            handleCloseOverlay();
        }
    }

    function handleJitsiApiReady(externalApi: IJitsiMeetExternalApi) {
        jitsiApiRef.current = externalApi;
        externalApi.addEventListener("videoConferenceJoined", () => setCallStatus("active"));
        externalApi.addEventListener("videoConferenceLeft", handleCloseOverlay);
        externalApi.addEventListener("readyToClose", handleCloseOverlay);
    }

    function handleDragStart(event: React.PointerEvent<HTMLElement>) {
        if (event.button !== 0 || (event.target instanceof Element && event.target.closest("button"))) return;

        const panel = panelRef.current;
        if (!panel) return;

        const rect = panel.getBoundingClientRect();
        dragStateRef.current = {
            height: rect.height,
            originX: rect.left,
            originY: rect.top,
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            width: rect.width,
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        event.preventDefault();
    }

    function handleDragMove(event: React.PointerEvent<HTMLElement>) {
        const dragState = dragStateRef.current;
        if (!dragState || dragState.pointerId !== event.pointerId) return;

        const nextX = clamp(
            dragState.originX + event.clientX - dragState.startX,
            PANEL_EDGE_GAP,
            Math.max(PANEL_EDGE_GAP, window.innerWidth - dragState.width - PANEL_EDGE_GAP)
        );
        const nextY = clamp(
            dragState.originY + event.clientY - dragState.startY,
            PANEL_EDGE_GAP,
            Math.max(PANEL_EDGE_GAP, window.innerHeight - dragState.height - PANEL_EDGE_GAP)
        );
        dragStateRef.current = { ...dragState, x: nextX, y: nextY };

        const panel = panelRef.current;
        if (panel) {
            panel.style.left = `${nextX}px`;
            panel.style.right = "auto";
            panel.style.top = `${nextY}px`;
        }
    }

    function handleDragEnd(event: React.PointerEvent<HTMLElement>) {
        const dragState = dragStateRef.current;
        if (!dragState || dragState.pointerId !== event.pointerId) return;

        dragStateRef.current = null;
        setPanelPosition({ x: dragState.x ?? dragState.originX, y: dragState.y ?? dragState.originY });
        event.currentTarget.releasePointerCapture?.(event.pointerId);
    }

    function handleResizeStart(event: React.PointerEvent<HTMLButtonElement>) {
        if (event.button !== 0) return;

        const panel = panelRef.current;
        if (!panel) return;

        const rect = panel.getBoundingClientRect();
        resizeStateRef.current = {
            height: rect.height,
            originX: rect.left,
            originY: rect.top,
            pointerId: event.pointerId,
            startHeight: rect.height,
            startX: Number.isFinite(event.clientX) ? event.clientX : 0,
            startY: Number.isFinite(event.clientY) ? event.clientY : 0,
            startWidth: rect.width,
            width: rect.width,
        };
        setPanelPosition({ x: rect.left, y: rect.top });
        event.currentTarget.setPointerCapture?.(event.pointerId);
        event.preventDefault();
    }

    function handleResizeMove(event: React.PointerEvent<HTMLButtonElement>) {
        const resizeState = resizeStateRef.current;
        if (!resizeState || resizeState.pointerId !== event.pointerId) return;

        const maxWidth = Math.max(0, window.innerWidth - resizeState.originX - PANEL_EDGE_GAP);
        const maxHeight = Math.max(0, window.innerHeight - resizeState.originY - PANEL_EDGE_GAP);
        const clientX = Number.isFinite(event.clientX) ? event.clientX : resizeState.startX;
        const clientY = Number.isFinite(event.clientY) ? event.clientY : resizeState.startY;
        const nextSize = {
            width: clamp(
                resizeState.startWidth + clientX - resizeState.startX,
                Math.min(MIN_PANEL_WIDTH, maxWidth),
                maxWidth
            ),
            height: clamp(
                resizeState.startHeight + clientY - resizeState.startY,
                Math.min(MIN_PANEL_HEIGHT, maxHeight),
                maxHeight
            ),
        };

        resizeStateRef.current = { ...resizeState, ...nextSize };
        const panel = panelRef.current;
        if (panel) {
            panel.style.width = `${nextSize.width}px`;
            panel.style.height = `${nextSize.height}px`;
        }
    }

    function handleResizeEnd(event: React.PointerEvent<HTMLButtonElement>) {
        const resizeState = resizeStateRef.current;
        if (!resizeState || resizeState.pointerId !== event.pointerId) return;

        resizeStateRef.current = null;
        setPanelSize({ width: resizeState.width, height: resizeState.height });
        event.currentTarget.releasePointerCapture?.(event.pointerId);
    }

    function buildReadyContent() {
        return (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 overflow-auto p-6 text-center">
                <p className="text-body-sm text-main-typography-system-negative">{callError || "ยังไม่ได้เชื่อมต่อห้องวิดีโอคอล"}</p>
            </div>
        );
    }

    function buildConnectingContent() {
        return (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 p-6 text-center" role="status">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-creation-primary-container-default border-t-creation-primary-solid-default motion-reduce:animate-none" />
                <div>
                    <p className="text-body-base font-medium text-main-typography-system-default">
                        กำลังเข้าสู่ห้องวิดีโอคอล
                    </p>
                    <p className="mt-1 text-body-sm text-main-typography-system-description">โปรดรอสักครู่...</p>
                </div>
            </div>
        );
    }

    function buildActiveContent() {
        if (!telemedMeeting) return null;

        return (
            <div className="relative min-h-0 flex-1 bg-slate-950">
                <JitsiMeeting
                    domain={telemedMeeting.domain}
                    roomName={telemedMeeting.room_name}
                    jwt={telemedMeeting.jwt}
                    configOverwrite={TELEMED_DOCTOR_JITSI_CONFIG}
                    interfaceConfigOverwrite={{
                        DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
                        SHOW_JITSI_WATERMARK: false,
                        HIDE_DEEP_LINKING_LOGO: true,
                    }}
                    userInfo={{ displayName: effectiveDoctorName, email: "" }}
                    onApiReady={handleJitsiApiReady}
                    getIFrameRef={(iframeRef) => {
                        if (!iframeRef) return;
                        iframeRef.style.height = "100%";
                        iframeRef.style.width = "100%";
                        iframeRef.style.border = "none";
                    }}
                />
            </div>
        );
    }

    function buildOverlayContent() {
        if (callStatus === "connecting") return buildConnectingContent();
        if (callStatus === "joining" || callStatus === "active") return buildActiveContent();
        return buildReadyContent();
    }

    function buildFloatingOverlay() {
        if (!isOverlayOpen) return null;

        const style: CSSProperties = {
            height: panelSize.height,
            left: panelPosition.x === null ? "auto" : panelPosition.x,
            maxHeight: `calc(100vh - ${PANEL_EDGE_GAP * 2}px)`,
            maxWidth: `calc(100vw - ${PANEL_EDGE_GAP * 2}px)`,
            minHeight: `min(${MIN_PANEL_HEIGHT}px, calc(100vh - ${PANEL_EDGE_GAP * 2}px))`,
            minWidth: `min(${MIN_PANEL_WIDTH}px, calc(100vw - ${PANEL_EDGE_GAP * 2}px))`,
            right: panelPosition.x === null ? PANEL_EDGE_GAP : "auto",
            top: panelPosition.y,
            width: panelSize.width,
        };

        return createPortal(
            <section
                ref={panelRef}
                role="dialog"
                aria-modal="false"
                aria-labelledby="telemed-overlay-title"
                className="fixed z-[120] flex overflow-hidden rounded-xl border border-main-border-system-default bg-main-background-system-default shadow-[0_12px_32px_rgba(15,23,42,0.18)]"
                style={style}
                data-testid="telemed-floating-overlay"
            >
                <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                    <header
                        className="flex min-h-12 touch-none select-none items-center gap-3 bg-creation-primary-solid-surface-default px-4 py-3 cursor-grab active:cursor-grabbing"
                        onPointerDown={handleDragStart}
                        onPointerMove={handleDragMove}
                        onPointerUp={handleDragEnd}
                        onPointerCancel={handleDragEnd}
                    >
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-main-background-accented-primary text-main-icon-accented-primary">
                                <Icon name="video-camera" size="md" />
                            </div>
                            <div className="min-w-0">
                                <h3 id="telemed-overlay-title" className="truncate text-body-base font-semibold text-main-typography-system-inverted">
                                    ระบบปรึกษาแพทย์ทางไกล
                                </h3>
                                <p className="truncate text-caption text-creation-primary-solid-on-surface-default">
                                    ลากแถบนี้เพื่อย้ายหน้าต่าง
                                </p>
                            </div>
                        </div>
                        <div
                            className="ml-auto flex shrink-0 items-center gap-1"
                            onPointerDown={(event) => event.stopPropagation()}
                        >
                            {callStatus === "active" ? (
                                <Badge text={formatTime(durationSeconds)} color="curious_blue" size="sm" />
                            ) : null}
                            {telemedMeeting?.invite_url && !callError ? (
                                <Tooltip
                                    direction="top"
                                    message={isInviteLinkCopied ? "คัดลอกลิงก์แล้ว" : "คัดลอกลิงก์ผู้ป่วย"}
                                    open={isInviteLinkCopied ? true : undefined}
                                >
                                    <Button
                                        hierarchy={isInviteLinkCopied ? "secondary" : "tertiary"}
                                        size="sm"
                                        icon={isInviteLinkCopied ? "check" : "share"}
                                        aria-label={isInviteLinkCopied ? "คัดลอกลิงก์ผู้ป่วยแล้ว" : "คัดลอกลิงก์สำหรับผู้ป่วย"}
                                        onClick={handleCopyInviteLink}
                                    />
                                </Tooltip>
                            ) : null}
                            {callError && !telemedMeeting ? (
                                <Button
                                    hierarchy="tertiary"
                                    size="sm"
                                    icon="refresh"
                                    aria-label="ลองเชื่อมต่อห้องวิดีโอคอลอีกครั้ง"
                                    title="ลองใหม่"
                                    onClick={handleJoinCall}
                                />
                            ) : null}
                            <Button
                                hierarchy="tertiary"
                                size="sm"
                                icon="x"
                                aria-label="วางสายและปิดหน้าต่างวิดีโอคอล"
                                title="วางสาย"
                                onClick={handleEndCall}
                            />
                        </div>
                    </header>
                    {buildOverlayContent()}
                </div>
                <button
                    type="button"
                    className="absolute bottom-0 right-0 z-10 flex h-8 w-8 touch-none cursor-se-resize items-center justify-center rounded-tl-md bg-main-background-system-default/90 text-main-icon-system-description shadow-sm transition-colors hover:bg-main-background-system-default focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-creation-primary-solid-default"
                    aria-label="ปรับขนาดหน้าต่างวิดีโอคอล"
                    title="ลากเพื่อปรับขนาด"
                    onPointerDown={handleResizeStart}
                    onPointerMove={handleResizeMove}
                    onPointerUp={handleResizeEnd}
                    onPointerCancel={handleResizeEnd}
                >
                    <Icon name="arrows-expand" size="sm" />
                </button>
            </section>,
            document.body
        );
    }

    return buildFloatingOverlay();
}

type TelemedVideoCallProps = {
    doctorName?: string;
    patient?: unknown;
    visit?: TelemedVisit;
    visitId?: TreatmentId | "";
};

export default function TelemedVideoCall({ visit, doctorName = "", visitId = "" }: TelemedVideoCallProps) {
    const { isTelemedOverlayOpen, openTelemedOverlay } = useTelemedOverlay();

    return (
        <section className="rounded-xl border border-main-border-system-default bg-main-background-system-default p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="text-body-sm font-semibold leading-snug text-main-typography-system-default">
                        ระบบปรึกษาแพทย์ทางไกล
                    </h3>
                </div>
                <Button
                    hierarchy={isTelemedOverlayOpen ? "secondary" : "primary"}
                    size="md"
                    leadingIcon="video-camera"
                    text="เริ่มวิดีโอคอล"
                    className="shrink-0"
                    disabled={isTelemedOverlayOpen}
                    onClick={() => openTelemedOverlay({ doctorName, visit, visitId })}
                />
            </div>
        </section>
    );
}
